/**
 * Kanal dosyalarini (Shopify export / Walmart item report) parse edip
 * normalize edilmis { marketplace_sku, price, compare_at, status, title, extra }
 * satirlarina cevirir. Parser tipi channels.parser_type'tan gelir.
 */

import type { ChannelBulkRow } from '../../services/api';

/**
 * RFC4180 benzeri, tirnak + tirnak-ici-newline destekli delimited parser.
 * Shopify Body (HTML) alani virgul/tirnak/newline icerir -> dogru parse sart.
 */
export function parseDelimited(text: string, delimiter: string): string[][] {
  // BOM temizle
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];

    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }   // escaped quote
        else inQuotes = false;
      } else {
        field += c;
      }
      continue;
    }

    if (c === '"') { inQuotes = true; }
    else if (c === delimiter) { row.push(field); field = ''; }
    else if (c === '\n') { row.push(field); rows.push(row); field = ''; row = []; }
    else if (c === '\r') { /* \r\n -> newline'i \n yakalar; tirnak disi \r at */ }
    else { field += c; }
  }
  // son alan/satir
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

function toObjects(rows: string[][]): Record<string, string>[] {
  if (rows.length === 0) return [];
  const headers = rows[0].map(h => h.trim());
  const out: Record<string, string>[] = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    if (r.length === 1 && r[0] === '') continue; // bos satir
    const obj: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) obj[headers[j]] = (r[j] ?? '').trim();
    out.push(obj);
  }
  return out;
}

function num(v: string | undefined): number | null {
  if (v === undefined || v === null) return null;
  const s = String(v).trim().replace(/[^0-9.-]/g, '');
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

export interface ParseOutcome {
  rows: ChannelBulkRow[];
  totalLines: number;   // basliklar haric ham satir
  skipped: number;      // SKU'su bos / atlanan
}

/**
 * Shopify product export (virgullu). Bir urunun varyantlari coklu satir,
 * gorsel satirlarinda Variant SKU bos -> atla. US fiyati = Variant Price.
 */
export function parseShopifyExport(text: string): ParseOutcome {
  const objs = toObjects(parseDelimited(text, ','));
  const rows: ChannelBulkRow[] = [];
  let skipped = 0;
  for (const o of objs) {
    const sku = (o['Variant SKU'] || '').trim();
    if (!sku) { skipped++; continue; }
    rows.push({
      marketplace_sku: sku,
      price: num(o['Variant Price']),
      compare_at: num(o['Variant Compare At Price']),
      status: o['Status'] || null,
      title: o['Title'] || null,
    });
  }
  return { rows, totalLines: objs.length, skipped };
}

/**
 * Walmart Item Report (noktali virgul ayracli).
 */
export function parseWalmartItemReport(text: string): ParseOutcome {
  const objs = toObjects(parseDelimited(text, ';'));
  const rows: ChannelBulkRow[] = [];
  let skipped = 0;
  for (const o of objs) {
    const sku = (o['SKU'] || '').trim();
    if (!sku) { skipped++; continue; }
    rows.push({
      marketplace_sku: sku,
      price: num(o['Price']),
      compare_at: num(o['MSRP']),
      status: o['Publish Status'] || null,
      title: o['Product Name'] || null,
      extra: {
        buybox: num(o['Buy Box Item Price']),
        competitor: num(o['Competitor Price']),
        item_id: o['Item ID'] || null,
      },
    });
  }
  return { rows, totalLines: objs.length, skipped };
}

export function parseByType(parserType: string, text: string): ParseOutcome {
  switch (parserType) {
    case 'shopify_export':       return parseShopifyExport(text);
    case 'walmart_item_report':  return parseWalmartItemReport(text);
    default:
      throw new Error(`Bu kanal icin dosya import destegi yok (parser: ${parserType})`);
  }
}
